import { take, put, spawn } from 'redux-saga/effects'
import { handleRequest } from './requests'
import { STATE_KEYS } from '../state/requests'
import {
    getCampaignOverview,
    GetCampaignOverviewResponse,
    getCampaignPerformance,
    GetCampaignPerformanceResponse,
    GetTaskPerformanceResponse,
    getTaskPerformance,
    GetIndividualPerformanceResponse,
    getIndividualPerformance,
    GetIndividualPerformanceStatsResponse,
    getIndividualPerformanceStats,
    getTopPerformers,
    GetTopPerformersResponse,
    updateAmbassadorFavorite,
    UpdateAmbassadorFavoriteRequest,
    UpdateAmbassadorFavoriteResponse,
    FilterOption,
    GetIndividualPerformanceFiltersRequest,
    getIndividualPerformanceFilters,
    downloadCampaignReport,
} from '../services/api/campaignReport'
import {
    CampaignReportActions,
    getCampaignOverviewSuccess,
    getCampaignPerformanceSuccess,
    getIndividualPerformanceSuccess,
    getTaskPerformanceSuccess,
    getTopPerformersSuccess,
    TopPerformersPayload,
    getIndividualPerformanceStatsSuccess,
    UpdateAmbassadorFavoritePayload,
    GetIndividualPerformanceFiltersPayload,
    UpdateAmbassadorFavoriteSuccess,
} from '../state/campaignReport'
import _ from 'lodash'
import { saveIndividualPerformanceFilters } from 'state/filter'
import { showSnackBar } from 'services/toast'
import i18n from 'config/i18n'

export function* handleGetCampaignOverview(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string }, GetCampaignOverviewResponse>({
        stateKey: STATE_KEYS.GetCampaignOverview,
        payload,
        ApiHandler: getCampaignOverview,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data

            yield put(getCampaignOverviewSuccess(data))
        },
    })
}

export function* watchGetCampaignOverviewFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_CAMPAIGN_OVERVIEW)
        yield spawn(handleGetCampaignOverview, action.payload)
    }
}

export function* handleGetCampaignPerformance(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string }, GetCampaignPerformanceResponse>({
        stateKey: STATE_KEYS.GetCampaignPerformance,
        payload,
        ApiHandler: getCampaignPerformance,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data

            yield put(getCampaignPerformanceSuccess(data))
        },
    })
}

export function* watchGetCampaignPerformanceFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_CAMPAIGN_PERFORMANCE)
        yield spawn(handleGetCampaignPerformance, action.payload)
    }
}

export function* handleGetTaskPerformance(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string }, GetTaskPerformanceResponse>({
        stateKey: STATE_KEYS.GetTaskPerformance,
        payload,
        ApiHandler: getTaskPerformance,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data

            yield put(getTaskPerformanceSuccess(data))
        },
    })
}

export function* watchGetTaskPerformanceFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_TASK_PERFORMANCE)
        yield spawn(handleGetTaskPerformance, action.payload)
    }
}

export function* handleGetIndividualPerformance(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string; limit: number }, GetIndividualPerformanceResponse>({
        stateKey: STATE_KEYS.GetIndividualPerformance,
        payload: { ...payload, limit: 80 },
        ApiHandler: getIndividualPerformance,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data

            yield put(getIndividualPerformanceSuccess(data))
        },
    })
}

export function* watchGetIndividualPerformanceFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE)
        yield spawn(handleGetIndividualPerformance, action.payload)
    }
}

export function* handleGetIndividualPerformanceStats(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string }, GetIndividualPerformanceStatsResponse>({
        stateKey: STATE_KEYS.GetIndividualPerformanceStats,
        payload,
        ApiHandler: getIndividualPerformanceStats,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data
            yield put(getIndividualPerformanceStatsSuccess(data))
        },
    })
}

export function* watchGetIndividualPerformanceStatsFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_STATS)
        yield spawn(handleGetIndividualPerformanceStats, action.payload)
    }
}

export function* handleUpdateAmbassadorFavorite(payload: UpdateAmbassadorFavoritePayload): any {
    yield handleRequest<UpdateAmbassadorFavoriteRequest, UpdateAmbassadorFavoriteResponse>({
        stateKey: STATE_KEYS.UpdateAmbassadorFavorite,
        payload,
        ApiHandler: updateAmbassadorFavorite,
        *onSuccess(response) {
            yield put(UpdateAmbassadorFavoriteSuccess(payload))

            if (payload.favorited) {
                showSnackBar({
                    title: i18n.t('validation.success.addedToAmbassadorList', {
                        brandName: payload.brand_name,
                    }),
                    type: 'positive',
                })
            } else {
                showSnackBar({
                    title: i18n.t('validation.success.removedToAmbassadorList', {
                        brandName: payload.brand_name,
                    }),
                    type: 'positive',
                })
            }
        },
        *onError() {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateAmbassadorFavoriteFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.UPDATE_AMBASSADOR_FAVORITE)
        yield spawn(handleUpdateAmbassadorFavorite, action.payload)
    }
}

export function* handleGetIndividualPerformanceFilters(payload: GetIndividualPerformanceFiltersPayload): any {
    yield handleRequest<GetIndividualPerformanceFiltersRequest, FilterOption>({
        stateKey: STATE_KEYS.GetIndividualPerformanceFilters,
        payload,
        ApiHandler: getIndividualPerformanceFilters,
        *onSuccess(response) {
            if (!response.data) return
            yield put(saveIndividualPerformanceFilters(response.data.data.results))
        },
    })
}

export function* watchGetIndividualPerformanceFiltersFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_FILTERS)
        yield spawn(handleGetIndividualPerformanceFilters, action.payload)
    }
}

export function* handleGetTopPerformers(payload: TopPerformersPayload): any {
    yield handleRequest<TopPerformersPayload, GetTopPerformersResponse>({
        stateKey: STATE_KEYS.GetTopPerformers,
        payload,
        ApiHandler: getTopPerformers,
        *onSuccess(response) {
            if (!response.data?.data) return
            if (_.isEmpty(response.data.data)) return

            const data = response.data.data
            yield put(getTopPerformersSuccess(data))
        },
    })
}

export function* watchGetTopPerformersFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.GET_TOP_PERFORMERS)
        yield spawn(handleGetTopPerformers, action.payload)
    }
}

export function* handleDownloadCampaignReport(payload: { uuid: string }): any {
    yield handleRequest<{ uuid: string }, void>({
        stateKey: STATE_KEYS.DownloadCampaignReport,
        payload,
        ApiHandler: downloadCampaignReport,
        *onSuccess(response) {
            showSnackBar({
                title: i18n.t('successMessage.downloadSuccessfully'),
                type: 'positive',
            })
        },
        *onError() {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}

export function* watchDownloadCampaignReportFlow(): any {
    while (true) {
        const action = yield take(CampaignReportActions.DOWNLOAD_CAMPAIGN_REPORT)
        yield spawn(handleDownloadCampaignReport, action.payload)
    }
}
